import { SlideSection } from "@NextSkyTheme/slide";
import { LazyLoader } from "@NextSkyTheme/lazy-load";
import * as NextSkyTheme from "@NextSkyTheme/global";
import { eventModal } from "@NextSkyTheme/modal";

class ActionSearch extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.input = this.querySelector('input[type="text"]');
    this.form = this.querySelector("form#search_mini_form");
    this.templateRecommendedDefault = this.querySelector(".template-recommended-default");
    this.templateRecommendedPopup = this.querySelector(".template-recommended-popup");
    this.predictiveSearchResults = this.querySelector(".predictive-search");
    this.searchKeyword = this.querySelector(".search-keyword");
    this.recommendSearch = this.querySelector(".recommend-search");
    this.input?.addEventListener(
      "input",
      NextSkyTheme.debounce((event) => {
        this.onInput(event);
      }, 300).bind(this)
    );
    this.input.addEventListener("click", this.hideAnimationText.bind(this));
    this.input.addEventListener("focus", this.hideAnimationText.bind(this));
    window.Shopify.designMode &&
      (document.addEventListener("shopify:section:select", (event) => {
        const currentTarget = event.target;
        if (JSON.parse(currentTarget.dataset.shopifyEditorSection).id === this.sectionId) {
          eventModal(this, "open", false);
        }
      }),
      document.addEventListener("shopify:section:deselect", () => {
        const searchSection = document.querySelector("action-search");
        if (searchSection) eventModal(searchSection, "close", false);
      }));
  }

  get type() {
    return this.getAttribute("data-type");
  }

  connectedCallback() {
    // Forward nav bar typing into the modal input to drive predictive search
    const navInput = document.getElementById("nav-search-input-field");
    if (navInput) {
      navInput.addEventListener("input", () => {
        if (this.input) {
          this.input.value = navInput.value;
          this.input.dispatchEvent(new InputEvent("input", { bubbles: true }));
        }
      });
      navInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (this.form && this.input) {
            this.input.value = navInput.value;
            this.form.submit();
          }
        }
      });
    }
    if (this.type === "popup") {
      this.innerWidth();
    }
  }

  _openSearch() {
    if (
      this.recommendSearch &&
      this.recommendSearch.querySelector("template") &&
      this.templateRecommendedDefault
    ) {
      this.updateProductRecommended();
    }
    eventModal(this, "open", false);
  }

  innerWidth() {
    let width = window.innerWidth;
    window.addEventListener("resize", () => {
      const newWidth = window.innerWidth;
      if (newWidth <= 1024 && width > 1024) this.actionSearchMobile();
      if (newWidth > 1024 && width <= 1024) this.actionSearchDesktop();
      width = newWidth;
    });
    if (width <= 1024) {
      this.actionSearchMobile();
    } else {
      this.actionSearchDesktop();
    }
  }

  updateProductRecommended() {
    if (this.classList.contains("search-type-popup") && this.templateRecommendedPopup) {
      const frag = this.templateRecommendedPopup.content.firstElementChild.cloneNode(true);
      this.recommendSearch.innerHTML = "";
      this.recommendSearch.appendChild(frag);
      this.initPopupSlider();
    } else {
      const frag = this.templateRecommendedDefault.content.firstElementChild.cloneNode(true);
      this.recommendSearch.innerHTML = "";
      this.recommendSearch.appendChild(frag);
    }
    this.recommendSearch.classList.add("update-recommended");
    new LazyLoader(".image-lazy-load");
  }

  initPopupSlider() {
    const setup = (attempt) => {
      const track = this.recommendSearch.querySelector(".rfy-track");
      const prev  = this.recommendSearch.querySelector(".rfy-arrow--prev");
      const next  = this.recommendSearch.querySelector(".rfy-arrow--next");
      if (!track || !prev || !next) return;
      if (!track.clientWidth && attempt < 12) {
        setTimeout(() => setup(attempt + 1), 80);
        return;
      }
      const freshPrev = prev.cloneNode(true);
      const freshNext = next.cloneNode(true);
      prev.replaceWith(freshPrev);
      next.replaceWith(freshNext);
      const atStart = () => track.scrollLeft <= 1;
      const atEnd   = () => track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      const syncArrows = () => {
        freshPrev.classList.toggle("is-disabled", atStart());
        freshNext.classList.toggle("is-disabled", atEnd());
      };
      freshPrev.addEventListener("click", () => {
        if (atStart()) return;
        track.scrollBy({ left: -track.clientWidth, behavior: "smooth" });
      });
      freshNext.addEventListener("click", () => {
        if (atEnd()) return;
        track.scrollBy({ left: track.clientWidth, behavior: "smooth" });
      });
      track.addEventListener("scroll", syncArrows, { passive: true });
      syncArrows();
      track.querySelectorAll(".rfy-atc").forEach((btn) => {
        btn.addEventListener("click", () => {
          const variantId = btn.dataset.variantId;
          if (!variantId) return;
          this._searchAddToCart(variantId, 1, btn);
        });
      });
    };
    setTimeout(() => setup(0), 60);
  }

  actionSearchMobile() {
    this.classList.remove("search-type-popup");
    this.classList.add("search-type-drawer");
    this.onInput();
    if (this.templateRecommendedDefault && this.recommendSearch.classList.contains("update-recommended")) {
      this.updateProductRecommended();
    }
  }

  actionSearchDesktop() {
    this.classList.remove("search-type-drawer");
    this.classList.add("search-type-popup");
    this.onInput();
    if (this.templateRecommendedDefault && this.recommendSearch.classList.contains("update-recommended")) {
      this.updateProductRecommended();
    }
  }

  onInput() {
    this.hideAnimationText();
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) {
      this.form.classList.remove("results", "on-input", "loading");
      return;
    }
    let predictiveSearch = "predictive-search-drawer";
    if (this.classList.contains("search-type-popup")) {
      predictiveSearch = "predictive-search-popup";
    }
    this.form.classList.add("loading", "on-input");
    if (this.predictiveSearchResults) {
      this.getSearchResults(searchTerm, predictiveSearch);
    }
  }

  getSearchResults(searchTerm, predictiveSearch) {
    const query = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${predictiveSearch}&resources[limit]=10&resources[limit_scope]=each`;
    fetch(query)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.form.classList.remove("loading", "results");
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(`#shopify-section-${predictiveSearch}`)?.innerHTML;
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = resultsMarkup;
        }
        this.form.classList.remove("loading");
        this.form.classList.add("results");
        this.querySelectorAll(".more-result-link").forEach((e) => {
          e.href = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`;
        });
      })
      .catch((error) => {
        this.form.classList.remove("loading", "results");
        throw error;
      });
  }

  async _searchAddToCart(variantId, quantity, btn) {
    if (btn.disabled || btn.getAttribute("aria-disabled") === "true") return;
    this._searchSetBtnState(btn, "loading");

    const cartEl = document.querySelector("cart-drawer") || document.querySelector("main-cart");

    // Ask Shopify to include re-rendered section HTML in the response —
    // same sections the theme's product-form requests so renderContents works.
    const sections =
      cartEl && typeof cartEl.getSectionsToRender === "function"
        ? cartEl.getSectionsToRender().map((s) => s.id)
        : ["cart-icon-bubble"];

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity,
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!res.ok) throw new Error(`${res.status}`);

      const response = await res.json();

      if (response.status) {
        // Shopify returned an error (e.g. sold out)
        this._searchSetBtnState(btn, "error");
        return;
      }

      // ── Primary path: use the section HTML from the response ────────────
      if (cartEl && typeof cartEl.renderContents === "function" && response.sections) {
        cartEl.renderContents(response);
      } else {
        // ── Fallback: update count bubble via cart.js ────────────────────
        fetch("/cart.js")
          .then((r) => r.json())
          .then(({ item_count }) => {
            document
              .querySelectorAll(
                "#cart-icon-bubble span, .cart-count-bubble span, [data-cart-count]"
              )
              .forEach((el) => (el.textContent = item_count));
          })
          .catch(() => {});
      }

      this._searchSetBtnState(btn, "added");

    } catch (err) {
      console.error("[Search ATC]", err);
      this._searchSetBtnState(btn, "error");
    }
  }

  _searchSetBtnState(btn, state) {
    const labels = {
      idle:    btn.dataset.labelDefault || btn.querySelector(".btn-label")?.textContent?.trim() || "Add to Cart",
      loading: btn.dataset.labelLoading || "Adding…",
      added:   btn.dataset.labelAdded   || "Added ✓",
      error:   btn.dataset.labelError   || "Try again",
    };

    // Keep the existing .btn-label span intact — just swap text inside it
    const labelEl = btn.querySelector(".btn-label");
    if (labelEl) {
      labelEl.textContent = labels[state] ?? labels.idle;
    }

    btn.setAttribute("aria-disabled", state === "loading" ? "true" : "false");
    btn.setAttribute("data-state", state);

    if (state === "added" || state === "error") {
      setTimeout(() => this._searchSetBtnState(btn, "idle"), 2500);
    }
  }

  hideAnimationText() {
    if (this.querySelector(".search-animation")) {
      this.querySelector(".search-animation").classList.add("hidden-important");
    }
  }
}
customElements.define("action-search", ActionSearch);

class SearchText extends HTMLElement {
  constructor() {
    super();
    Motion.inView(this, this.init.bind(this));
  }

  async init() {
    await this.animation(this.getAttribute("data-text"));
  }

  async animation(text) {
    (this.innerHTML = text),
      await Motion.animate(
        this,
        { width: [0, `${this.scrollWidth + 1}px`] },
        { duration: 0.7, delay: 0.5 }
      ).finished,
      this.closest(".search-animation")
        .querySelector(".text-cursor")
        .classList.remove("hidden-important");
  }
}
customElements.define("search-text", SearchText);

// Nav search bar typewriter animation — animates input placeholder
class NavSearchKeyword {
  constructor(el) {
    this.el = el;
    this.prefix = el.dataset.prefix || "";
    const raw = el.dataset.keywords || "";
    this.keywords = raw
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    if (!this.keywords.length) return;
    this.index = 0;
    this.charIndex = 0;
    this.deleting = false;
    this.paused = false;
    this.typingSpeed = 90;
    this.deletingSpeed = 55;
    this.pauseAfterType = 1600;
    this.pauseAfterDelete = 350;
    this._timeout = null;

    el.addEventListener("focus", () => {
      this.paused = true;
      clearTimeout(this._timeout);
      el.placeholder = "";
    });
    el.addEventListener("blur", () => {
      if (!el.value) {
        this.paused = false;
        this.charIndex = 0;
        this.deleting = false;
        this.tick();
      }
    });

    this.tick();
  }

  tick() {
    if (this.paused) return;
    const word = this.keywords[this.index];
    if (!this.deleting) {
      // Typing
      this.charIndex++;
      this.el.placeholder = this.prefix + word.slice(0, this.charIndex);
      if (this.charIndex === word.length) {
        this.deleting = true;
        this._timeout = setTimeout(() => this.tick(), this.pauseAfterType);
        return;
      }
      this._timeout = setTimeout(() => this.tick(), this.typingSpeed);
    } else {
      // Deleting
      this.charIndex--;
      this.el.placeholder = this.prefix + word.slice(0, this.charIndex);
      if (this.charIndex === 0) {
        this.deleting = false;
        this.index = (this.index + 1) % this.keywords.length;
        this._timeout = setTimeout(() => this.tick(), this.pauseAfterDelete);
        return;
      }
      this._timeout = setTimeout(() => this.tick(), this.deletingSpeed);
    }
  }

  destroy() {
    clearTimeout(this._timeout);
  }
}

// Init when DOM is ready
(function initNavSearchKeyword() {
  // Header bar input (desktop)
  const headerInput = document.querySelector(".nav-search-input[data-keywords]");
  if (headerInput && headerInput.dataset.keywords.trim()) {
    new NavSearchKeyword(headerInput);
  }

  // Search page input (#Search-In-Page)
  const pageInput = document.getElementById("Search-In-Page");
  if (pageInput && pageInput.dataset.keywords?.trim()) {
    new NavSearchKeyword(pageInput);
  }

  // Modal input (mobile drawer) — init once, restart when drawer opens
  const modalInput = document.getElementById("Search-In-Modal");
  if (!modalInput || !modalInput.dataset.keywords?.trim()) return;

  let modalKw = null;

  const startModalAnimation = () => {
    if (modalKw) modalKw.destroy();
    modalInput.value = "";
    modalInput.placeholder = "";
    modalKw = new NavSearchKeyword(modalInput);
  };

  const stopModalAnimation = () => {
    if (modalKw) { modalKw.destroy(); modalKw = null; }
    modalInput.placeholder = "";
  };

  // Restart animation every time the search drawer/popup opens
  const searchEl = document.querySelector("action-search");
  if (searchEl) {
    // MutationObserver watches for the "active" class being added
    new MutationObserver(() => {
      if (searchEl.classList.contains("active")) {
        // Small delay so the drawer is visible before animation starts
        setTimeout(startModalAnimation, 200);
      } else {
        stopModalAnimation();
      }
    }).observe(searchEl, { attributes: true, attributeFilter: ["class"] });
  }
})();

// Open dropdown on click, close on outside click
(function initNavSearchDropdown() {
  const trigger = document.getElementById("search-icon-bubble");
  const searchEl = document.querySelector("action-search");
  if (!trigger || !searchEl) return;

  const lockScroll   = () => document.body.classList.add("search-scroll-lock");
  const unlockScroll = () => document.body.classList.remove("search-scroll-lock");

  const closeSearch = () => {
    searchEl.classList.remove("active");
    searchEl.classList.add("content-hidden");
    unlockScroll();
  };

  // Flag: ignore the very next document click that opens the form
  // (the same click that triggers open also bubbles to document)
  let justOpened = false;

  trigger.addEventListener("click", () => {
    justOpened = true;
    searchEl.classList.remove("content-hidden");
    searchEl.classList.add("active");
    lockScroll();
    if (typeof searchEl.updateProductRecommended === "function") {
      searchEl.updateProductRecommended();
    }
  });

  document.addEventListener("click", (e) => {
    // Never close on clicks that originated from the trigger
    if (trigger.contains(e.target)) return;

    // Ignore the bubbled click from the open action
    if (justOpened) { justOpened = false; return; }

    // Only act while the search is actually open
    if (!searchEl.classList.contains("active")) return;

    // Stay open when clicking inside the search panel
    const modalInner = searchEl.querySelector(".modal-inner");
    const isInsidePanel = modalInner
      ? modalInner.contains(e.target)
      : searchEl.contains(e.target);

    if (!isInsidePanel) closeSearch();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && searchEl.classList.contains("active")) {
      closeSearch();
      trigger.focus();
    }
  });
})();
